/* =========================================================================
   Village Green — Reusable Quiz Engine
   Dependency-free. Config-driven multi-step assessment + personalized report.

   A quiz config registers itself:  VGQuiz.register('glp1', { ... })
   A page mounts it:                <div class="vgq-root" data-quiz-id="glp1"></div>
   The engine auto-mounts every .vgq-root on DOMContentLoaded.

   Question types: 'single' (radio), 'multi' (checkbox), 'grouped-multi'.
   Report is assembled by config.buildReport(answers) -> { blocks:[...], payload:{} }.
   Lead capture pushes to Klaviyo (onsite) if present; no-ops gracefully otherwise.
   ========================================================================= */
(function (window, document) {
  'use strict';

  var VGQuiz = { configs: {} };

  VGQuiz.register = function (id, config) {
    config.id = id;
    VGQuiz.configs[id] = config;
    // If a root for this quiz already rendered (config loaded after mount), (re)mount it.
    var pending = document.querySelectorAll('.vgq-root[data-quiz-id="' + id + '"]:not([data-vgq-mounted])');
    for (var i = 0; i < pending.length; i++) new Quiz(pending[i], config).render();
  };

  /* ----------------------------- helpers ------------------------------ */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function el(html) { var t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstChild; }
  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  /* ----------------------------- Quiz class --------------------------- */
  function Quiz(root, config) {
    this.root = root;
    this.config = config;
    this.answers = {};
    this.step = 0;                 // 0..N-1 = questions, N = email gate
    this.questions = config.questions || [];
    this.gateIndex = this.questions.length;
    root.setAttribute('data-vgq-mounted', '1');
  }

  Quiz.prototype.totalSteps = function () { return this.questions.length + 1; };

  Quiz.prototype.render = function () {
    var c = this.config;
    this.root.classList.add('vgq-root');
    this.root.innerHTML =
      '<div class="vgq-shell">' +
        '<div class="vgq-brand">' + esc(c.brand || '') + '</div>' +
        '<h2 class="vgq-title">' + esc(c.title || '') + '</h2>' +
        (c.intro ? '<div class="vgq-intro">' + esc(c.intro) + '</div>' : '') +
        '<div class="vgq-progress">' +
          '<div class="vgq-progress-track"><div class="vgq-progress-fill" data-vgq="fill"></div></div>' +
          '<div class="vgq-progress-label" data-vgq="plabel"></div>' +
        '</div>' +
        '<div data-vgq="steps"></div>' +
        '<div class="vgq-error" data-vgq="error"></div>' +
        '<div class="vgq-nav" data-vgq="nav"></div>' +
        '<div class="vgq-report" data-vgq="report"></div>' +
      '</div>';
    this.$ = {
      fill: this.root.querySelector('[data-vgq="fill"]'),
      plabel: this.root.querySelector('[data-vgq="plabel"]'),
      steps: this.root.querySelector('[data-vgq="steps"]'),
      error: this.root.querySelector('[data-vgq="error"]'),
      nav: this.root.querySelector('[data-vgq="nav"]'),
      report: this.root.querySelector('[data-vgq="report"]'),
      progress: this.root.querySelector('.vgq-progress')
    };
    this.buildSteps();
    this.goto(0);
  };

  Quiz.prototype.buildSteps = function () {
    var self = this;
    this.questions.forEach(function (q, i) { self.$.steps.appendChild(self.renderQuestion(q, i)); });
    this.$.steps.appendChild(this.renderGate());
  };

  Quiz.prototype.renderQuestion = function (q, i) {
    var step = document.createElement('div');
    step.className = 'vgq-step'; step.setAttribute('data-step', i);
    var multi = q.type === 'multi' || q.type === 'grouped-multi';
    var inputType = multi ? 'checkbox' : 'radio';
    var html = '<span class="vgq-q-num">Question ' + (i + 1) + ' of ' + this.questions.length + '</span>' +
      '<div class="vgq-q-label">' + esc(q.label) + '</div>' +
      (q.help ? '<div class="vgq-q-help">' + esc(q.help) + '</div>' : '') +
      '<div class="vgq-opts">';
    if (q.type === 'grouped-multi') {
      (q.groups || []).forEach(function (g) {
        html += '<div class="vgq-group">' + (g.label ? '<div class="vgq-group-label">' + esc(g.label) + '</div>' : '');
        g.options.forEach(function (o) { html += optHtml(q.id, o, inputType); });
        html += '</div>';
      });
    } else {
      (q.options || []).forEach(function (o) { html += optHtml(q.id, o, inputType); });
    }
    html += '</div>';
    step.innerHTML = html;

    // Each .vgq-opt is a <label> wrapping its input, so a click anywhere in the
    // row natively toggles the control and fires 'change'. Bind ONLY to change —
    // binding a click handler too would double-fire onSelect (skipping single
    // questions and cancelling multi-select toggles).
    var self = this;
    step.querySelectorAll('.vgq-opt input').forEach(function (input) {
      input.addEventListener('change', function () { self.onSelect(q, input.closest('.vgq-opt')); });
    });
    return step;
  };

  function optHtml(qid, o, inputType) {
    var label = '<label class="vgq-opt"><input type="' + inputType + '" name="' + esc(qid) + '" value="' +
      esc(o.value) + '"> <span class="vgq-opt-text">' + esc(o.label) + '</span></label>';
    if (!o.examples) return label;
    // Optional expandable examples (brand and generic names), outside the
    // <label> so opening them never toggles the answer. Inline styles keep
    // quiz-engine.css unchanged for the live GLP-1 quiz (2026-10-05).
    return '<div class="vgq-opt-wrap">' + label +
      '<details class="vgq-opt-examples" style="margin:-4px 0 10px 2.2em;font-size:.9em;">' +
      '<summary style="cursor:pointer;opacity:.8;">' + esc(o.examplesLabel || 'Common names') + '</summary>' +
      '<div style="padding:4px 0 2px;">' + esc(o.examples) + '</div></details></div>';
  }

  Quiz.prototype.onSelect = function (q, opt) {
    var step = opt.closest('.vgq-step');
    var multi = q.type === 'multi' || q.type === 'grouped-multi';
    if (multi && q.exclusive && opt.querySelector('input').checked) {
      // an "exclusive" option (e.g. "None of the above") clears the others
      var val = opt.querySelector('input').value;
      if (q.exclusive.indexOf(val) > -1) {
        step.querySelectorAll('input').forEach(function (inp) {
          if (inp.value !== val) { inp.checked = false; inp.closest('.vgq-opt').classList.remove('is-selected'); }
        });
      } else {
        // selecting a normal option clears any exclusive ones
        q.exclusive.forEach(function (ev) {
          var x = step.querySelector('input[value="' + cssEsc(ev) + '"]');
          if (x) { x.checked = false; x.closest('.vgq-opt').classList.remove('is-selected'); }
        });
      }
    }
    step.querySelectorAll('.vgq-opt').forEach(function (o) {
      o.classList.toggle('is-selected', o.querySelector('input').checked);
    });
    this.hideError();
    // auto-advance single-choice questions for a snappier feel.
    // Debounced via a single timer so repeated onSelect calls collapse to one advance.
    if (!multi && this.config.autoAdvance !== false) {
      var self = this;
      clearTimeout(this._advanceTimer);
      this._advanceTimer = setTimeout(function () { if (self.step < self.gateIndex) self.next(); }, 240);
    }
  };

  function cssEsc(v) { return String(v).replace(/["\\]/g, '\\$&'); }

  Quiz.prototype.renderGate = function () {
    var g = this.config.emailGate || {};
    var ctx = (window.VGQ_CONTEXT && window.VGQ_CONTEXT.customer) || null;
    var pfName = esc(ctx && ctx.firstName ? ctx.firstName : '');
    var pfEmail = esc(ctx && ctx.email ? ctx.email : '');
    var heading = (ctx && g.headingReturning) ? g.headingReturning : (g.heading || 'Your personalized report is ready.');
    var step = document.createElement('div');
    step.className = 'vgq-step'; step.setAttribute('data-step', this.gateIndex);
    step.innerHTML =
      '<div class="vgq-q-label">' + esc(heading) + '</div>' +
      (g.subheading ? '<div class="vgq-q-help">' + esc(g.subheading) + '</div>' : '') +
      '<div class="vgq-gate-field"><label>First name</label>' +
        '<input class="vgq-input" data-vgq="firstName" type="text" autocomplete="given-name" placeholder="First name" value="' + pfName + '"></div>' +
      '<div class="vgq-gate-field"><label>Email address</label>' +
        '<input class="vgq-input" data-vgq="email" type="email" autocomplete="email" placeholder="you@example.com" value="' + pfEmail + '"></div>' +
      this.optinHtml() +
      (g.consent ? '<div class="vgq-consent">' + esc(g.consent) + '</div>' : '');
    return step;
  };

  /* Opt-in box (2026-10-30). Shown when config.optin.enabled, or for tests
     when the page address carries ?<testParam>=1. Uses the answer-option
     markup (label.vgq-opt) so it looks like the quiz's own checkboxes. */
  Quiz.prototype.optinShown = function () {
    var o = this.config.optin;
    if (!o) return false;
    if (o.enabled) return true;
    return !!o.testParam && new RegExp('[?&]' + o.testParam + '=1(&|$)').test(window.location.search || '');
  };

  Quiz.prototype.optinHtml = function () {
    if (!this.optinShown()) return '';
    var o = this.config.optin, benefits = o.benefits || [];
    return '<label class="vgq-opt vgq-optin"><input type="checkbox" data-vgq="optin"' + (o.checked ? ' checked' : '') + '> ' +
      '<span class="vgq-opt-text">' + esc(o.label) + '</span></label>' +
      (benefits.length ? '<ul class="vgq-optin-benefits">' +
        benefits.map(function (b) { return '<li>' + esc(b) + '</li>'; }).join('') + '</ul>' : '');
  };

  Quiz.prototype.goto = function (n) {
    this.step = n;
    var steps = this.$.steps.querySelectorAll('.vgq-step');
    steps.forEach(function (s, i) { s.classList.toggle('is-active', i === n); });
    var total = this.totalSteps();
    this.$.fill.style.width = Math.round(((n) / total) * 100) + '%';
    this.$.plabel.textContent = (n < this.gateIndex)
      ? ('Step ' + (n + 1) + ' of ' + total)
      : 'Last step';
    this.renderNav();
    this.hideError();
    try { this.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
  };

  Quiz.prototype.renderNav = function () {
    var self = this, isGate = this.step === this.gateIndex;
    var back = this.step > 0
      ? '<button class="vgq-btn vgq-btn-ghost" data-vgq="back">Back</button>' : '';
    var nextLabel = isGate ? (this.config.emailGate && this.config.emailGate.submitLabel || 'See my results') : 'Continue';
    this.$.nav.innerHTML = back +
      '<button class="vgq-btn vgq-btn-primary" data-vgq="next">' + esc(nextLabel) + '</button>';
    var b = this.$.nav.querySelector('[data-vgq="back"]');
    if (b) b.addEventListener('click', function () { self.prev(); });
    this.$.nav.querySelector('[data-vgq="next"]').addEventListener('click', function () {
      isGate ? self.submit() : self.next();
    });
  };

  Quiz.prototype.collect = function (q) {
    var inputs = this.$.steps.querySelector('.vgq-step[data-step="' + q._i + '"]').querySelectorAll('input:checked');
    var vals = []; inputs.forEach(function (i) { vals.push(i.value); });
    return (q.type === 'multi' || q.type === 'grouped-multi') ? vals : (vals[0] || null);
  };

  Quiz.prototype.next = function () {
    var q = this.questions[this.step]; q._i = this.step;
    var val = this.collect(q);
    var empty = (val == null) || (Array.isArray(val) && val.length === 0);
    if (empty && q.required !== false) { return this.showError(q.error || 'Please choose an option to continue.'); }
    this.answers[q.id] = val;
    // Optional early route: q.route(value, answers) returns { text, cta,
    // continueLabel } to send the person elsewhere before the report.
    if (typeof q.route === 'function' && !this._routeSkipped) {
      var stop = null;
      try { stop = q.route(val, this.answers); } catch (e) { console.warn('[VGQuiz] route failed', e); }
      if (stop) return this.renderRoute(stop);
    }
    this.goto(this.step + 1);
  };

  Quiz.prototype.renderRoute = function (stop) {
    var self = this, from = this.step;
    this.$.steps.style.display = 'none';
    this.$.nav.style.display = 'none';
    this.hideError();
    this.$.report.innerHTML = '<div class="vgq-block">' +
      (stop.heading ? '<h3>' + esc(stop.heading) + '</h3>' : '') +
      '<p class="vgq-lead">' + esc(stop.text) + '</p>' +
      (stop.cta && stop.cta.url ? '<a class="vgq-cta-btn vgq-cta-btn-solid" href="' + esc(stop.cta.url) + '" style="color:#fff;">' + esc(stop.cta.label) + '</a>' : '') +
      (stop.continueLabel ? ' <button class="vgq-btn vgq-btn-ghost" data-vgq="continue">' + esc(stop.continueLabel) + '</button>' : '') +
      '</div>';
    this.$.report.classList.add('is-shown');
    var c = this.$.report.querySelector('[data-vgq="continue"]');
    if (c) c.addEventListener('click', function () {
      self._routeSkipped = true;  // they chose to continue; do not route again
      self.$.report.classList.remove('is-shown');
      self.$.report.innerHTML = '';
      self.$.steps.style.display = '';
      self.$.nav.style.display = '';
      self.goto(from + 1);
    });
    try { this.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
  };

  Quiz.prototype.prev = function () {
    if (this.step === 0) return;
    if (this.step <= this.gateIndex) this.goto(this.step - 1);
  };

  Quiz.prototype.showError = function (msg) { this.$.error.textContent = msg; this.$.error.classList.add('is-shown'); };
  Quiz.prototype.hideError = function () { this.$.error.classList.remove('is-shown'); };

  Quiz.prototype.submit = function () {
    var gate = this.$.steps.querySelector('.vgq-step[data-step="' + this.gateIndex + '"]');
    var firstName = (gate.querySelector('[data-vgq="firstName"]').value || '').trim();
    var email = (gate.querySelector('[data-vgq="email"]').value || '').trim();
    if (!validEmail(email)) return this.showError('Please enter a valid email so we can send your full report.');
    // optin: true or false when the opt-in box is on the page, null when it is not.
    var box = gate.querySelector('[data-vgq="optin"]');
    this.lead = { firstName: firstName, email: email, optin: box ? box.checked : null };
    var result;
    try { result = this.config.buildReport(this.answers) || {}; }
    catch (e) { console.error('[VGQuiz] buildReport failed', e); return this.showError('Something went wrong building your results. Please try again.'); }
    var payload = result.payload || {};
    if (box) payload.optin = box.checked; // stored as GLP1_optin (contact and profile)
    this.capture(payload);
    this.renderReport(result, firstName);
  };

  /* ----------------------------- lead capture ------------------------- */
  Quiz.prototype.capture = function (payload) {
    var c = this.config, kl = c.klaviyo || {};
    var prefix = kl.propertyPrefix || '';
    var custom = {}; // quiz result props (phase, symptomCluster, ...) namespaced by prefix
    for (var k in payload) if (payload.hasOwnProperty(k)) custom[prefix + k] = payload[k];

    // klaviyo: false sends nothing. null would fall through to the onsite
    // script below, which the store's Klaviyo embed loads on every page.
    if (c.klaviyo === false) {
      console.info('[VGQuiz] Klaviyo disabled for this quiz — nothing sent.');
    } else if (kl.companyId) {
      // Preferred: Klaviyo client-side API (public key, CORS). Works regardless of
      // whether onsite klaviyo.js is in scope; creates/updates the profile + records
      // the event in one call. Returns 202 on success.
      this.klaviyoClientEvent(kl, custom, payload);
      // Subscribe to the list (email marketing consent collected at the gate) so
      // flows can send + Klaviyo syncs the profile to Shopify as a customer.
      // An unticked opt-in box skips this step: the subscription stays as it was.
      if (kl.listId && this.lead.optin !== false) this.klaviyoSubscribe(kl);
    } else {
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          var props = { email: this.lead.email, first_name: this.lead.firstName };
          for (var p in custom) props[p] = custom[p];
          window.klaviyo.identify(props);
          if (kl.eventName) window.klaviyo.track(kl.eventName, payload);
        } else if (window._learnq) {
          var lprops = { '$email': this.lead.email, '$first_name': this.lead.firstName };
          for (var lp in custom) lprops[lp] = custom[lp];
          window._learnq.push(['identify', lprops]);
          if (kl.eventName) window._learnq.push(['track', kl.eventName, payload]);
        } else {
          console.info('[VGQuiz] Klaviyo not detected — lead captured locally only.', this.lead, payload);
        }
      } catch (e) { console.warn('[VGQuiz] Klaviyo capture error', e); }
    }

    // Optional Shopify customer capture (off by default — many stores have form
    // captcha that rejects a programmatic /contact POST). Prefer Klaviyo→Shopify sync.
    this.captureShopifyCustomer(payload);

    // Ticked opt-in box: the store's newsletter form records the consent.
    if (this.lead.optin === true) this.optinSignup();

    // Omnisend: consent through the store's own newsletter signup, then the
    // quiz event through Omnisend's onsite script. Only a quiz that sets
    // config.omnisend uses this (added 2026-10-05).
    if (c.omnisend) this.omnisendCapture(payload);

    // Custom hook for future integrations (e.g. H+ join by email).
    if (typeof c.onCapture === 'function') { try { c.onCapture(this.lead, payload); } catch (e) {} }
    try { this.root.dispatchEvent(new CustomEvent('vgquiz:lead', { bubbles: true, detail: { lead: this.lead, payload: payload } })); } catch (e) {}
    publishCompleted(c.id);
  };

  /* Tells Shopify Customer events that a quiz was completed, so a custom
     pixel can listen (Meta Lead, Barham Marketing, 2026-10-07). It carries
     the quiz id only: no answers and no email, because the answers are
     health information. Shopify loads its analytics a few seconds after the
     page, so this retries for up to 15 seconds. */
  function publishCompleted(quizId, tries) {
    tries = tries || 0;
    try {
      var a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') {
        a.publish('vgquiz:completed', { quiz: String(quizId || '') });
        return;
      }
    } catch (e) { return; }
    if (tries < 15) setTimeout(function () { publishCompleted(quizId, tries + 1); }, 1000);
  }

  Quiz.prototype.klaviyoClientEvent = function (kl, custom, payload) {
    var profileAttrs = { email: this.lead.email, properties: custom };
    if (this.lead.firstName) profileAttrs.first_name = this.lead.firstName;
    var body = { data: { type: 'event', attributes: {
      properties: payload,
      metric: { data: { type: 'metric', attributes: { name: kl.eventName } } },
      profile: { data: { type: 'profile', attributes: profileAttrs } }
    } } };
    try {
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(kl.companyId), {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/json', revision: kl.revision || '2024-10-15' },
        body: JSON.stringify(body)
      }).then(function (r) { console.info('[VGQuiz] Klaviyo client event →', r.status); })
        .catch(function (e) { console.warn('[VGQuiz] Klaviyo client event failed', e); });
    } catch (e) { console.warn('[VGQuiz] Klaviyo client event error', e); }
  };

  Quiz.prototype.klaviyoSubscribe = function (kl) {
    var profileAttrs = { email: this.lead.email };
    if (this.lead.firstName) profileAttrs.first_name = this.lead.firstName;
    var body = { data: { type: 'subscription', attributes: {
      custom_source: kl.subscribeSource || 'Quiz',
      profile: { data: { type: 'profile', attributes: profileAttrs } }
    }, relationships: { list: { data: { type: 'list', id: kl.listId } } } } };
    try {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(kl.companyId), {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/json', revision: kl.revision || '2024-10-15' },
        body: JSON.stringify(body)
      }).then(function (r) { console.info('[VGQuiz] Klaviyo subscribe →', r.status); })
        .catch(function (e) { console.warn('[VGQuiz] Klaviyo subscribe failed', e); });
    } catch (e) { console.warn('[VGQuiz] Klaviyo subscribe error', e); }
  };

  /* Omnisend capture. No server and no secret key:
       1. Shopify's newsletter signup (form_type=customer) records email
          marketing consent on a Shopify customer, with tags only. Omnisend's
          Shopify connection syncs that consent.
       2. omnisend.push(['track', name, {contact, properties}]) records the
          quiz event. The onsite script accepts contact.email in the event, so
          it does not wait for identifyContact. Health answers go to Omnisend
          only, never into Shopify.
     live: false sends only for addresses matching testPattern. */
  Quiz.prototype.omnisendCapture = function (payload) {
    var os = this.config.omnisend, email = this.lead.email;
    if (!os.live && !(os.testPattern && os.testPattern.test(email))) {
      console.info('[VGQuiz] Omnisend not live for this quiz — nothing sent.');
      return;
    }
    // include (2026-10-14): when set, only payload keys that match it are sent.
    // The Drug-Nutrient quiz sends only its results-email fields, not the raw
    // answers. If nothing matches (the under-18 stop), no event and no signup.
    var keep = function (key) { return !os.include || os.include.test(key); };
    var props = {}, prefix = os.propertyPrefix || '', kept = 0;
    for (var k in payload) if (payload.hasOwnProperty(k) && keep(k)) { props[prefix + k] = omnisendValue(payload[k]); kept++; }
    if (os.include && !kept) {
      console.info('[VGQuiz] Omnisend: no results fields for this answer set — nothing sent.');
      return;
    }
    if (os.shopifySignup !== false) {
      this.captureShopifyCustomer(payload, { tags: os.tags || [] });
    }
    var contact = { email: email };
    if (this.lead.firstName) contact.firstName = this.lead.firstName;
    // contactPrefix: also store the answers on the Omnisend contact as custom
    // properties (for example GLP1_phase), like Klaviyo profile properties.
    // Omnisend block conditions read contact properties, not event ones.
    if (os.contactPrefix) {
      contact.customProperties = {};
      for (var ck in payload) if (payload.hasOwnProperty(ck) && keep(ck)) contact.customProperties[os.contactPrefix + ck] = omnisendValue(payload[ck]);
    }
    try {
      window.omnisend = window.omnisend || [];
      // origin is required; Omnisend's custom-event guide uses 'api' (2026-10-05).
      window.omnisend.push(['track', os.eventName, { origin: os.origin || 'api', contact: contact, properties: props }]);
      console.info('[VGQuiz] Omnisend event queued:', os.eventName);
    } catch (e) { console.warn('[VGQuiz] Omnisend capture error', e); }
  };

  // Omnisend event properties: string, number, boolean, or a list of strings.
  function omnisendValue(v) {
    if (Array.isArray(v)) return v.map(function (x) { return typeof x === 'string' ? x : JSON.stringify(x); });
    if (v !== null && typeof v === 'object') return JSON.stringify(v);
    return v;
  }

  Quiz.prototype.captureShopifyCustomer = function (payload, override) {
    var sc = override || this.config.shopifyCustomer;
    if (!sc || sc.enabled === false) return;
    var ctx = (window.VGQ_CONTEXT && window.VGQ_CONTEXT.customer) || null;
    // Already logged in as this exact email and already tagged? Nothing to do.
    if (ctx && ctx.email && ctx.email.toLowerCase() === this.lead.email.toLowerCase() &&
        sc.skipIfTagged && ctx.tags && ctx.tags.indexOf(sc.skipIfTagged) > -1) return;

    var body = new URLSearchParams();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', this.lead.email);
    if (this.lead.firstName) body.append('contact[first_name]', this.lead.firstName);
    var tags = (sc.tags || []).slice();
    if (sc.tagPhase && payload.phase) tags.push((sc.tagPrefix || '') + payload.phase);
    if (tags.length) body.append('contact[tags]', tags.join(','));
    try {
      fetch(sc.endpoint || '/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
        body: body.toString(), credentials: 'same-origin'
      }).then(function (r) {
        console.info('[VGQuiz] Shopify customer capture (' + (ctx ? 'logged-in' : 'guest') + ') status', r.status);
      }).catch(function (e) { console.warn('[VGQuiz] Shopify customer capture failed', e); });
    } catch (e) { console.warn('[VGQuiz] Shopify customer capture error', e); }
  };

  /* Opt-in signup (2026-10-30): the store's own newsletter form
     ({% form 'customer' %} in sections/quiz.liquid), sent into a hidden frame
     so the results stay on screen. A scripted submit must wire up Shopify's
     CAPTCHA first with Shopify.captcha.protect (shopify.dev, "CAPTCHA").
     Without it the form is still sent, and Shopify rejects it. Before switch
     day (enabled: false) only test addresses are sent. A failure here never
     blocks the report. */
  Quiz.prototype.optinSignup = function () {
    var o = this.config.optin, email = this.lead.email;
    if (!o.enabled && !(o.testPattern && o.testPattern.test(email))) {
      console.info('[VGQuiz] Opt-in box in test mode — newsletter signup skipped for this address.');
      return;
    }
    try {
      var form = document.getElementById('vgq-optin-form');
      if (!form) { console.warn('[VGQuiz] Opt-in: newsletter form not on the page — signup skipped.'); return; }
      form.elements['contact[email]'].value = email;
      form.elements['contact[first_name]'].value = this.lead.firstName || '';
      form.elements['contact[tags]'].value = (o.tags || []).join(',');
      if (!this._optinFrame) {
        var frame = this._optinFrame = document.createElement('iframe');
        frame.name = 'vgq-optin-frame';
        frame.title = 'Newsletter signup';
        frame.hidden = true;
        frame.setAttribute('aria-hidden', 'true');
        // Same origin, so the frame's address shows Shopify's answer.
        frame.addEventListener('load', function () {
          try {
            var href = frame.contentWindow.location.href;
            if (href !== 'about:blank') console.info('[VGQuiz] Newsletter signup →', /customer_posted=true/.test(href) ? 'accepted' : href);
          } catch (e) {}
        });
        document.body.appendChild(frame);
      }
      form.target = this._optinFrame.name;
      var captcha = window.Shopify && window.Shopify.captcha;
      if (captcha && typeof captcha.protect === 'function') {
        captcha.protect(form, function () { form.submit(); });
      } else {
        console.warn('[VGQuiz] Opt-in: no Shopify CAPTCHA on the page — form sent without it.');
        form.submit();
      }
    } catch (e) { console.warn('[VGQuiz] Opt-in signup error', e); }
  };

  /* ----------------------------- report render ------------------------ */
  Quiz.prototype.renderReport = function (result, firstName) {
    var html = '';
    if (result.phaseBadge || result.phaseTitle) {
      html += (result.phaseBadge ? '<span class="vgq-phase-badge">' + esc(result.phaseBadge) + '</span>' : '') +
        (result.phaseTitle ? '<h2 class="vgq-phase-title">' + esc(result.phaseTitle) + '</h2>' : '') +
        (result.phaseSub ? '<div class="vgq-phase-sub">' + esc(result.phaseSub) + '</div>' : '');
    }
    if (result.intro) html += '<p class="vgq-lead">' + esc(result.intro) + '</p>';
    // A quiz may render its own block kinds via config.renderBlock(block, esc).
    // Returning null or undefined falls back to the built-in kinds below.
    var custom = this.config.renderBlock;
    (result.blocks || []).forEach(function (b) {
      var out = null;
      if (custom) {
        try { out = custom(b, esc); }
        catch (e) { console.error('[VGQuiz] renderBlock failed for ' + b.kind, e); out = ''; }
      }
      html += (out != null) ? out : renderBlock(b);
    });
    this.$.report.innerHTML = html +
      '<div class="vgq-restart"><button class="vgq-btn vgq-btn-ghost" data-vgq="restart">Retake the assessment</button></div>';

    // hide the quiz UI, reveal the report
    this.$.steps.style.display = 'none';
    this.$.nav.style.display = 'none';
    this.$.error.style.display = 'none';
    this.$.progress.style.display = 'none';
    this.$.fill.style.width = '100%';
    this.$.report.classList.add('is-shown');
    var self = this;
    this.$.report.querySelector('[data-vgq="restart"]').addEventListener('click', function () { self.restart(); });
    try { this.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
  };

  /* Product image for a supplement card (2026-10-31). sections/quiz.liquid
     prints window.VGQ_CONTEXT.productImages, a map from product handle to
     image URL. A card whose link has no mapped handle (a collection, or a
     product that is gone) gets no image and renders as before. */
  function productImage(url) {
    var imgs = window.VGQ_CONTEXT && window.VGQ_CONTEXT.productImages;
    var m = imgs && /\/products\/([^\/?#]+)/.exec(url || '');
    return m && Object.prototype.hasOwnProperty.call(imgs, m[1]) ? imgs[m[1]] : '';
  }

  function renderBlock(b) {
    switch (b.kind) {
      case 'heading': return '<div class="vgq-block"><h3>' + esc(b.text) + '</h3></div>';
      case 'phase':
        return '<div class="vgq-block">' +
          (b.heading ? '<h3>' + esc(b.heading) + '</h3>' : '') +
          (b.paragraphs || []).map(function (p, i) {
            return '<p' + (i === 0 ? ' class="vgq-lead"' : '') + '>' + p + '</p>'; }).join('') +
          (b.navigating ? '<h4>What your body is navigating right now</h4><ul class="vgq-list">' +
            b.navigating.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ul>' : '') +
          (b.priorities ? '<h4>Your priorities right now</h4><ul class="vgq-list">' +
            b.priorities.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ul>' : '') +
          '</div>';
      case 'supplements':
        return '<div class="vgq-block">' +
          (b.heading ? '<h3>' + esc(b.heading) + '</h3>' : '') +
          (b.intro ? '<p>' + b.intro + '</p>' : '') +
          (b.items || []).map(function (it) {
            var name = it.url ? '<a href="' + esc(it.url) + '" target="_blank" rel="noopener">' + esc(it.name) + '</a>' : esc(it.name);
            var shop = it.url ? '<span class="vgq-shop-link">Shop ›</span>' : '';
            // The image repeats the name link, so screen readers and the tab key skip it.
            // A failed load removes the image and has-img, so the card falls back to today's layout.
            var img = productImage(it.url);
            var pic = img ? '<a class="vgq-supp-img" href="' + esc(it.url) + '" target="_blank" rel="noopener" tabindex="-1" aria-hidden="true">' +
              '<img src="' + esc(img) + '" alt="" width="96" height="96" loading="lazy" ' +
              'onerror="var a=this.parentNode;a.parentNode.classList.remove(\'has-img\');a.remove()"></a>' : '';
            return '<div class="vgq-supp"><div class="vgq-supp-name' + (img ? ' has-img' : '') + '">' + pic + name + shop + '</div>' +
              '<div class="vgq-supp-why">' + (it.why || '') + '</div></div>';
          }).join('') + '</div>';
      case 'info': return '<div class="vgq-info">' + (b.html || '') + '</div>';
      case 'note': return '<div class="vgq-note">' + (b.html || '') + '</div>';
      case 'timing': return '<div class="vgq-timing">' + (b.html || '') + '</div>';
      case 'med':
        return '<div class="vgq-note"><strong>' + esc(b.title || 'A note about your medication') + '</strong><br>' +
          (b.html || '') + (b.rec ? '<br><em>' + esc(b.rec) + '</em>' : '') + '</div>';
      case 'cta':
        var ctaBtns = b.buttons ? '<div class="vgq-cta-btns">' + b.buttons.map(function (btn) {
          return '<a class="vgq-cta-btn ' + (btn.style === 'outline' ? 'vgq-cta-btn-outline' : 'vgq-cta-btn-solid') + '" href="' +
            esc(btn.url || '#') + '"' + (btn.external ? ' target="_blank" rel="noopener"' : '') + '>' + esc(btn.label) + '</a>';
        }).join('') + '</div>' : '';
        // A buttons-only CTA (no heading/body) is a section shop-link — render it as a
        // plain inline pill, not the full filled conversion card.
        if (!b.heading && !b.body) return '<div class="vgq-cta-inline">' + ctaBtns + '</div>';
        return '<div class="vgq-cta">' +
          (b.heading ? '<h4>' + esc(b.heading) + '</h4>' : '') +
          (b.body ? '<p>' + b.body + '</p>' : '') +
          ctaBtns + '</div>';
      case 'disclaimer': return '<div class="vgq-disclaimer">' + (b.html || '') + '</div>';
      default: return '';
    }
  }

  Quiz.prototype.restart = function () {
    this.answers = {}; this.step = 0; this.lead = null; this._routeSkipped = false;
    this.$.report.classList.remove('is-shown'); this.$.report.innerHTML = '';
    this.$.steps.style.display = ''; this.$.nav.style.display = '';
    this.$.error.style.display = ''; this.$.progress.style.display = '';
    this.$.steps.querySelectorAll('input').forEach(function (i) { i.checked = false; });
    this.$.steps.querySelectorAll('.vgq-opt').forEach(function (o) { o.classList.remove('is-selected'); });
    this.goto(0);
  };

  /* ----------------------------- auto-mount --------------------------- */
  function mountAll() {
    var roots = document.querySelectorAll('.vgq-root[data-quiz-id]:not([data-vgq-mounted])');
    for (var i = 0; i < roots.length; i++) {
      var id = roots[i].getAttribute('data-quiz-id');
      var cfg = VGQuiz.configs[id];
      if (cfg) new Quiz(roots[i], cfg).render();
      // if config not yet loaded, register() will mount it when it arrives
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll);
  else mountAll();

  window.VGQuiz = VGQuiz;
})(window, document);
